import { FC, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogDescription,
	DialogHeader,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import EPasswordInput from '@/components/everridge/EPasswordInput';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { cn } from '@/lib/utils';
import { useUpdatePasswordApi } from '@/features/account/hooks/useUpdatePasswordApi';

type PasswordDto = {
	currentPassword: string;
	newPassword: string;
};

const PasswordFormSchema = z
	.object({
		currentPassword: z
			.string({ message: 'Current password is needed.' })
			.min(1, 'Current password is needed.'),
		newPassword: z
			.string({ message: 'New password is needed.' })
			.min(1, 'New password is needed.'),
		confirmNewPassword: z
			.string({ message: 'Current password is needed.' })
			.min(1, 'New password is needed.'),
	})
	.refine(
		({ newPassword, confirmNewPassword }) =>
			newPassword === confirmNewPassword,
		{
			message: "New passwords don't match",
			path: ['confirmNewPassword'],
		},
	);

type PasswordFormData = z.infer<typeof PasswordFormSchema>;

type Props = {
	onClose: () => void;
	isMobile?: boolean;
};

const EChangePasswordModal: FC<PropsWithChildren<Props>> = ({
	onClose,
	isMobile,
}) => {
	const [isOpen, setIsOpen] = useState(true);
	const { t } = useTranslation();

	const { control, handleSubmit, setError } = useForm<PasswordFormData>({
		resolver: zodResolver(PasswordFormSchema),
	});

	const { updatePassword } = useUpdatePasswordApi({
		onError: checkCurrentPassword,
	});

	function checkCurrentPassword(errorMessage: string) {
		if (errorMessage === 'Invalid current password') {
			setError('currentPassword', {
				message: 'Current password is wrong.',
			});
		}
	}

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const submitFormHandler = async (form: PasswordFormData) => {
		const dto: PasswordDto = {
			currentPassword: form.currentPassword,
			newPassword: form.newPassword,
		};

		await updatePassword(dto);
		onClose();
	};

	return (
		<Dialog
			open={isOpen}
			onOpenChange={handleClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t('Change password')}
						<DialogClose
							className={cn(
								'ml-auto flex items-center justify-center cursor-pointer',
								isMobile &&
									'size-11 border border-popover-100 rounded-full',
							)}>
							<Cancel_01_Icon />
						</DialogClose>
					</DialogTitle>
					<DialogDescription />
				</DialogHeader>
				<div className='flex flex-col gap-4'>
					<EPasswordInput
						control={control}
						fieldName={'currentPassword'}
						label={t('Current password')}
						placeholder={t('Enter current password')}
					/>
					<EPasswordInput
						control={control}
						fieldName={'newPassword'}
						label={t('New password')}
						placeholder={t('Enter new password')}
					/>
					<EPasswordInput
						control={control}
						fieldName={'confirmNewPassword'}
						label={t('Confirm password')}
						placeholder={t('Confirm new password')}
					/>
				</div>
				<DialogFooter
					className={cn('justify-end', isMobile && ' mt-auto')}>
					<Button
						variant='outline'
						onClick={onClose}>
						{t('Cancel')}
					</Button>
					<Button onClick={handleSubmit(submitFormHandler)}>
						{t('Change password')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EChangePasswordModal;
